import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal, ModalProps } from 'antd'

import {
  usePersonsServiceAcceptPersonTransferRequestEndpoint,
  usePersonsServiceGetListPersonTransferRequestEndpointKey,
  usePersonsServiceRejectPersonTransferRequestEndpoint
} from 'src/api/queries'
import { SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'

type Props = {
  id?: number
  type: 'approve' | 'reject' | null
  onCancel: () => void
} & ModalProps

export const ActionModal = ({ id, type, onCancel }: Props) => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const { t } = useLocales()

  const isReject = type === 'reject'

  const { mutate: rejectTransfer, isPending: isRejectPending } =
    usePersonsServiceRejectPersonTransferRequestEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [usePersonsServiceGetListPersonTransferRequestEndpointKey]
        })
        onCancel()
      }
    })

  const { mutate: approveTransfer, isPending: isApprovePending } =
    usePersonsServiceAcceptPersonTransferRequestEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [usePersonsServiceGetListPersonTransferRequestEndpointKey]
        })
        onCancel()
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const payload = {
      id: String(id),
      requestBody: {
        comment: values.comment
      }
    }

    isReject ? rejectTransfer(payload) : approveTransfer(payload)
  }

  return (
    <Modal
      open
      maskClosable={false}
      footer={
        <Flex align="center" justify="center" gap={8}>
          <Button onClick={onCancel}>{t('users.cancel')}</Button>

          <SaveConfirm
            title={`${t('stock_applications.confirm')}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isApprovePending || isRejectPending }}
          >
            <Button type="primary" danger={isReject}>
              {t('stock_applications.confirm')}
            </Button>
          </SaveConfirm>
        </Flex>
      }
      title={t(
        isReject ? 'transfer.reject_transfer' : 'transfer.approve_transfer'
      )}
      onCancel={onCancel}
    >
      <Form layout="vertical" form={form} requiredMark={false}>
        <Flex gap={24}>
          <Flex vertical gap={24} className="full-width">
            <Form.Item name="comment" label={t('common.leave_comment')}>
              <Input.TextArea rows={5} />
            </Form.Item>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
